import { ARRIVAL_REASON } from '@/features/arrival/types';
import { useKioskSession } from '@/lib/kioskSession';

/**
 * **Is this a pay-card ERRAND off the arrival menu, rather than the tail of an application?**
 *
 * One signal, and three things hang off it on every pay-card screen: whether the journey rail is drawn,
 * whether Back is offered, and where a finished registration lands. Figma draws each of those screens
 * twice for exactly this reason — `12 · Scan` / `R2 · Pay card — scan`, `13 · Pay card linked` /
 * `R3 · Pay card linked` — and the prototype states the rule in prose: *"the same screen can mean
 * different things: a returning candidate replacing a pay card is NOT being hired, so in hub context that
 * screen carries no rail even though it is part of the apply flow for a new hire."*
 *
 * ⚠️ **It is a hook rather than a line repeated per screen because getting it wrong has already shipped
 * twice.** `PayCardScreen` selected it for the rail alone, and every successful registration went to
 * `SCREEN.finish` — whose no-submission branch says *"please see a representative"* — so a returning
 * associate who linked a card correctly was told to sit down and wait for somebody (Operator,
 * 2026-09-08). The same screen's Back was a second copy of the question, and `useBack` is a third. One
 * signal, several exits; each new screen on this route is another chance to wire it to one of them.
 *
 * **The condition is the arrival REASON, never merely "did they see the menu".** A returning associate who
 * picks *Search for a job* and applies reaches these screens as the tail of an application, and that IS a
 * journey step — rail included, and the flow continues forward rather than back to the menu. Only somebody
 * who came in to link a card, and nothing else, is off the journey.
 */
export function usePayCardErrand(): boolean {
  return useKioskSession((state) => state.arrival?.reason === ARRIVAL_REASON.payCard);
}
